import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";

const SPECIAL_CHAR_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

function StrengthCheck({ password }) {
    const checks = [
        [password.length >= 8, "8+ characters"],
        [/[A-Za-z]/.test(password), "One letter"],
        [/\d/.test(password), "One number"],
        [SPECIAL_CHAR_RE.test(password), "Special char (!@#…)"],
    ];
    return (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 px-1 text-[11px]">
            {checks.map(([ok, label]) => (
                <span key={label} className={`flex items-center gap-1 ${ok ? "text-green-pioneer" : "text-text-secondary/60"}`}>
                    {ok ? "✓" : "○"} {label}
                </span>
            ))}
        </div>
    );
}

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const isValid = (
        password.length >= 8 &&
        /[A-Za-z]/.test(password) &&
        /\d/.test(password) &&
        SPECIAL_CHAR_RE.test(password) &&
        password === confirm
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!token) { setError("Missing reset token. Please use the link from your email."); return; }
        if (password !== confirm) { setError("Passwords don't match."); return; }
        if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
        if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) { setError("Include at least one letter and one number."); return; }
        if (!SPECIAL_CHAR_RE.test(password)) { setError("Include at least one special character (e.g. @, #, !)."); return; }

        setSubmitting(true);
        try {
            await api.post("/api/auth/reset-password", { token, new_password: password });
            setDone(true);
            toast.success("Password reset! Please sign in with your new password.");
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (!err.response) {
                setError("Connection error. Check your internet and try again.");
            } else {
                setError(detail || "Reset failed. The link may have expired — please request a new one.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-dvh flex items-center justify-center px-4 py-24">
                <GlassCard className="p-8 max-w-md w-full text-center" hover={false}>
                    <AlertCircle size={32} className="text-red-starter mx-auto mb-3" />
                    <h2 className="text-text-primary font-semibold mb-2">Invalid reset link</h2>
                    <p className="text-text-secondary text-sm mb-4">This link is missing a reset token.</p>
                    <Link to="/forgot-password" className="text-blue-primary hover:underline text-sm">
                        Request a new reset link
                    </Link>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="min-h-dvh flex items-center justify-center px-4 py-24">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md"
            >
                <Link to="/login" className="inline-flex items-center gap-1.5 text-text-secondary hover:text-text-primary text-sm mb-6 transition-colors">
                    <ArrowLeft size={14} /> Back to sign in
                </Link>

                <GlassCard className="p-8" hover={false}>
                    {done ? (
                        <div className="text-center py-4">
                            <CheckCircle2 size={40} className="text-green-pioneer mx-auto mb-4" />
                            <p className="text-text-primary font-semibold mb-2">Password changed!</p>
                            <p className="text-text-secondary text-sm">Redirecting you to sign in…</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-text-primary mb-2">Set a new password</h1>
                                <p className="text-text-secondary text-sm">
                                    Choose a strong password for your GenZ Credit Score account.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 bg-red-starter/10 border border-red-starter/20 rounded-xl px-4 py-2.5">
                                        <AlertCircle size={14} className="text-red-starter shrink-0" />
                                        <p className="text-xs text-red-starter">{error}</p>
                                    </div>
                                )}

                                <div className="glass-input-wrap">
                                    <Lock size={16} className="field-icon" />
                                    <input
                                        type={showPw ? "text" : "password"}
                                        placeholder="New password"
                                        value={password}
                                        onChange={e => { setPassword(e.target.value); if (error) setError(""); }}
                                        className="glass-input text-sm"
                                        required
                                        autoFocus
                                    />
                                    <button type="button" onClick={() => setShowPw(v => !v)} className="eye-toggle" tabIndex={-1}>
                                        {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                    </button>
                                </div>

                                {password.length > 0 && <StrengthCheck password={password} />}

                                <div className="glass-input-wrap">
                                    <Lock size={16} className="field-icon" />
                                    <input
                                        type="password"
                                        placeholder="Confirm new password"
                                        value={confirm}
                                        onChange={e => { setConfirm(e.target.value); if (error) setError(""); }}
                                        className="glass-input text-sm"
                                        required
                                    />
                                </div>
                                {confirm.length > 0 && password !== confirm && (
                                    <p className="text-[11px] text-red-starter px-1">Passwords don't match</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting || !isValid}
                                    className="w-full bg-blue-primary text-white py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all disabled:opacity-50"
                                >
                                    {submitting ? "Resetting…" : "Reset password"}
                                </button>
                            </form>
                        </>
                    )}
                </GlassCard>
            </motion.div>
        </div>
    );
}
